import {
  ApiUrls,
  BetBuilderRemovalConditionEnum,
  EventStatus,
  OddMatchConditionEnum,
  StateActionTypes,
} from "../../types/enums";
import { convertToBetSlipSelection, dispatchCustomEvent } from "./bet-slip";
import { findOddMatchCondition } from "./action-state";

export const convertSelectionToBetBuilderBet = (
  selection: IBetSlipSelection,
) => {
  const {
    betTypeId,
    eventId,
    betTypeName,
    oddName,
    oddLine,
    oddPrice,
    gameTime,
    homeName,
    awayName,
    pid,
    event,
    sourceModule,
  } = selection;

  const bet = {
    id: betTypeId,
    eventId,
    eventName: `${homeName} - ${awayName}`,
    eventNameTranslation: `${homeName} - ${awayName}`,
    marketName: betTypeName,
    marketNameTranslation: betTypeName,
    selectionName: oddName,
    selectionNameTranslation: oddName,
    odds: oddPrice,
    date: gameTime,
    sportName: event?.sn || "",
    sportId: event?.sid || 0,
    ...(oddLine && { line: oddLine }),
    ...(pid && { pid }),
    ...(sourceModule && { sourceModule }),
  };

  return bet;
};

// Separate API function
// Fixed fetchEventData function
export const fetchEventData = (
  eventId: string,
  betTypeId: string,
): Promise<any> => {
  return fetch(
    `${ApiUrls.GET_EVENTS}?eventIds=${eventId}&betTypeIds=${betTypeId}`,
    { method: "GET" },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((apiResponse) => {
      // Extract the first event from the data array
      if (apiResponse.data && apiResponse.data.length > 0) {
        return apiResponse.data[0]; // Return the actual event object
      }
      throw new Error("No event data found in API response");
    });
};

// Synchronous conversion directly from IBetBuilderBet — no API call needed.
// All fields required by convertToBetSlipSelection are already present on the bet
// or can be safely defaulted (st:0 prematch, s:0 active since it's still in betslip).
export const convertBetBuilderBetToSelectionDirect = (
  bet: IBetBuilderBet,
): IBetSlipSelection | null => {
  if (!bet) return null;

  const [homeName = "", awayName = ""] = bet.eventName.split(" - ");

  const selectedEvent: ISelectedEvent = {
    selection: {
      id: Number(bet.id),
      data: {
        _: {
          id: Number(bet.id),
          n: bet.marketName,
          o: 1,
          s: 0,
        },
      },
      odds: [],
      selected: {
        id: bet.selectionName,
        n: bet.selectionNameTranslation,
        p: String(bet.odds),
        s: 0,
        ...(bet.line && { l: bet.line }),
      },
    },
    event: {
      id: Number(bet.eventId),
      gn: 0,
      data: {
        id: Number(bet.eventId),
        h: homeName.trim(),
        a: awayName.trim(),
        gt: bet.date || "",
        st: 0,
      } as IEvent,
    },
  };

  return convertToBetSlipSelection(selectedEvent);
};

// Synchronous conversion function that takes pre-fetched data
export const convertBetBuilderBetToSelection = (
  removed: IBetBuilderBet,
  eventData: any,
): IBetSlipSelection | null => {
  const normalSel = removed ? removed : null;
  if (!normalSel || !eventData) {
    return null;
  }

  const bet = eventData.bts?.find((x: any) => x.id === normalSel.id);

  // Make the comparison case-insensitive
  const odd = bet?.odds?.find(
    (x: any) =>
      x.id?.toLowerCase() === normalSel.selectionName?.toLowerCase() &&
      x.l === normalSel.line,
  );

  if (!bet || !odd) {
    console.warn(
      `Bet or odd not found for betId: ${normalSel.id}, selectionName: ${normalSel.selectionName}, line: ${normalSel.line}`,
      {
        bet,
        searchingFor: { id: normalSel.selectionName, line: normalSel.line },
        availableOdds: bet?.odds,
      },
    );
    return null;
  }

  // Rest of the function remains the same...
  const selectedEvent: ISelectedEvent = {
    selection: {
      id: bet.id,
      data: {
        _: {
          id: bet.id,
          n: bet.n,
          o: bet.o,
          s: odd.s,
        },
      },
      odds: bet.odds,
      selected: odd,
    },
    event: {
      id: eventData.id,
      gn: eventData.gn,
      data: eventData,
    },
  };

  return convertToBetSlipSelection(selectedEvent);
};

export const getBetBuilderItems = (bbState: IBetBuilderState) => {
  const grouped: { [eventId: string]: [] } = bbState.bets?.reduce(
    (acc: any, bet) => {
      if (!acc[bet.eventId]) {
        acc[bet.eventId] = [];
      }
      acc[bet.eventId].push({
        BetTypeId: bet.id,
        BetTypeName: bet.marketName,
        OddKey: bet.selectionName
          ? bet.selectionName.charAt(0).toUpperCase() +
            bet.selectionName.slice(1)
          : bet.selectionName,
        OddPrice: bet.odds,
        DisplayOrder: 1,
        OddLine: bet?.line || null,
      });
      return acc;
    },
    {},
  );

  const betBuilderItems: IBetBuilderEvent[] = [];
  bbState.totalOdds?.forEach((odd) => {
    const exist = betBuilderItems.find((x) => x.EventId === +odd.eventId);
    if (!exist) {
      const event = bbState.bets.find((x) => +x.eventId === +odd.eventId);
      if (event) {
        const teams = event.eventName.split(" - ");
        betBuilderItems.push({
          EventId: +odd.eventId,
          SelectionKey: "BetBuilder" + odd.eventId,
          CalculationType: 2,
          CalculatedOdds: odd.odd,
          Home: teams[0].trim(),
          Away: teams[1].trim(),
          GameTime: event.date as string,
          Selections: grouped[odd.eventId],
        });
      }
    }
  });

  return betBuilderItems;
};

const findMatchingBBOdd = (
  betType: IBetType,
  bet: IBetBuilderBet,
): IOdd | undefined =>
  betType.odds?.find((odd) => {
    const key = bet.selectionName?.toLowerCase();
    const oddMatches =
      odd.id?.toLowerCase() === key || odd.n?.toLowerCase() === key;
    if (!oddMatches) return false;
    return bet.line
      ? String(bet.line) === String(odd.l || "")
      : !odd.l || odd.l === "";
  });

const resolveBBBetFlags = (
  condition: OddMatchConditionEnum,
): { suspended: boolean; closed: boolean } => {
  switch (condition) {
    case OddMatchConditionEnum.MatchedClosed:
    case OddMatchConditionEnum.UnmatchedClosed:
      return { suspended: false, closed: true };
    case OddMatchConditionEnum.MatchedSuspended:
    case OddMatchConditionEnum.UnmatchedSuspended:
      return { suspended: true, closed: false };
    default:
      return { suspended: false, closed: false };
  }
};

export const updateBetBuilderSelections = (
  bets: IBetBuilderBet[],
  updatedEvents: IEvent[],
): IBetBuilderBet[] => {
  const updates: IBetBuilderBet[] = [];

  if (!bets?.length) return updates;

  bets.forEach((bet) => {
    const updatedEvent = updatedEvents.find((e) => e.id === +bet.eventId);
    if (!updatedEvent) return;

    // Support both 'st' and 's' for event status (API may vary)
    const eventStatus =
      updatedEvent.st ?? (updatedEvent as any).s ?? EventStatus.Prematch;
    const betType = updatedEvent.bts?.find((bt) => bt.id == +bet.id);
    const matchingOdd = betType ? findMatchingBBOdd(betType, bet) : undefined;

    // Falls back to bet-type/event status when the feed omits the specific
    // odd once it's suspended/closed (a common real-world payload shape).
    const condition = findOddMatchCondition({
      matchingOddStatus: matchingOdd?.s,
      betTypeStatus: betType?.s,
      eventStatus,
    });
    if (condition === OddMatchConditionEnum.Unchanged) return;

    const { suspended, closed } = resolveBBBetFlags(condition);
    const newOddPrice = matchingOdd ? parseFloat(matchingOdd.p) : bet.odds;
    const eventClosed = eventStatus === EventStatus.Closed;

    const priceChanged = bet.odds !== newOddPrice;
    const statusChanged =
      suspended !== !!bet.suspended ||
      closed !== !!bet.closed ||
      eventClosed !== !!bet.eventClosed;

    if (priceChanged || statusChanged) {
      updates.push({
        ...bet,
        odds: newOddPrice,
        previouseOdds: bet.odds,
        suspended,
        closed,
        eventClosed,
      });
    }
  });

  if (updates.length > 0) {
    dispatchCustomEvent(StateActionTypes.BB_RECALCULATE, updates);
  }

  return updates;
};

// A CLOSED event invalidates the whole combo (a BB combo needs every leg's
// market intact), so it's dropped entirely. A suspended/closed
// LEG (its own odd or bet type, event otherwise unaffected) only drops that
// one bet, leaving the rest of the combo in place.
export const resolveBetBuilderRemovalCondition = (
  bet: IBetBuilderBet,
): BetBuilderRemovalConditionEnum => {
  if (bet.eventClosed) {
    return BetBuilderRemovalConditionEnum.RemoveWholeEvent;
  }
  if (bet.suspended || bet.closed) {
    return BetBuilderRemovalConditionEnum.RemoveLegOnly;
  }
  return BetBuilderRemovalConditionEnum.NoRemoval;
};

export const partitionBetBuilderRemovals = (
  bets: IBetBuilderBet[],
): { eventIdsToRemove: number[]; legsToRemove: IBetBuilderBet[] } => {
  const eventIdsToRemove = new Set<number>();
  const legsToRemove: IBetBuilderBet[] = [];

  bets.forEach((bet) => {
    switch (resolveBetBuilderRemovalCondition(bet)) {
      case BetBuilderRemovalConditionEnum.RemoveWholeEvent:
        eventIdsToRemove.add(Number(bet.eventId));
        break;
      case BetBuilderRemovalConditionEnum.RemoveLegOnly:
        legsToRemove.push(bet);
        break;
      case BetBuilderRemovalConditionEnum.NoRemoval:
      default:
        break;
    }
  });

  return { eventIdsToRemove: [...eventIdsToRemove], legsToRemove };
};

// Shared by every caller that only has access to dispatchCustomEvent (i.e.
// reducers) rather than a direct handle on the Bet Builder widget instance.
export const dispatchBetBuilderRemovals = (bets: IBetBuilderBet[]): void => {
  const { eventIdsToRemove, legsToRemove } = partitionBetBuilderRemovals(bets);

  eventIdsToRemove.forEach((eventId) =>
    dispatchCustomEvent(StateActionTypes.REMOVE_BB_EVENT, eventId),
  );
  if (legsToRemove.length > 0) {
    dispatchCustomEvent(StateActionTypes.REMOVE_BB_BETS, legsToRemove);
  }
};

// Bet-placement recovery (additionalInfo from placebetsport/executeOrder)
// already flags a closed event for regular selections via applyRecoveryChange
// — this gives Bet Builder bets on that same event the identical signal,
// flagged through BB_RECALCULATE rather than removed directly, so it goes
// through the same AOC-gated removal path (BetBuilderProvider / accept click)
// regular selections already use.
export const applyRecoveryChangesToBetBuilder = (
  bets: IBetBuilderBet[],
  changes: RecoveryChange[],
): void => {
  if (!bets?.length) return;

  const updates = bets.reduce((acc: IBetBuilderBet[], bet) => {
    const change = changes.find(
      (c) => (c.ev_id ?? c.ev_Id) === +bet.eventId,
    );
    const eventClosed = change?.ev_s === EventStatus.Closed;
    if (eventClosed && !bet.eventClosed) {
      acc.push({ ...bet, eventClosed: true, closed: true });
    }
    return acc;
  }, []);

  if (updates.length > 0) {
    dispatchCustomEvent(StateActionTypes.BB_RECALCULATE, updates);
  }
};
